import type { HTMLComponent } from "@commercekit/react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import * as React from "react";
import {
  ContactUsAction,
  useLocale,
  useLocalePath,
  baseWovarContactUsValidator,
  b2bWovarContactUsValidator,
  orderWovarContactUsValidator,
  productWovarContactUsValidator,
} from "@commercekit/remix/index";
import { ValidatedForm } from "remix-validated-form";
import WvFormHiddenInputs from "../atoms/WvFormHiddenInputs";
import { useContactUs } from "~/hooks/useContactUs";
import { useWvContactForm } from "~/hooks/useDataLayer";
import WvField from "../molecules/WvField";
import WvInputField from "../molecules/WvInputField";
import WvTextareaField from "../atoms/WvTextarea";
import { GoogleReCaptchaCheckbox } from "@google-recaptcha/react";
import { useNavigation } from "@remix-run/react";
import WvSelectField from "../molecules/WvSelectField";
import { useMemo, useRef, useState } from "react";

const WvContactUsForm: HTMLComponent<{
  title: string;
  linkToAfterSubmit: string;
  captchaSiteKey: string;
}> = ({ title, linkToAfterSubmit, captchaSiteKey }) => {
  const { $pt } = usePrismicLabels();
  const { formId } = useContactUs();
  const gtmContactForm = useWvContactForm();
  const localePath = useLocalePath();
  const { formAction } = useNavigation();
  const formType = useRef<string>("order");
  const [formRef, setFormRef] = useState(Math.random());
  const loading = React.useMemo(
    () => formAction === localePath("/contact"),
    [localePath, formAction]
  );
  const recaptchaValue = useRef<string | null>(null);
  
  const handleRecaptchaChange = React.useCallback(
    (value: string | null) =>
      document
        ?.getElementById("recaptchaInput")
        ?.setAttribute("value", value ?? ""),
    []
  );

  const formTypes = [
    { name: $pt("form_type_option_order"), value: "order" },
    { name: $pt("form_type_option_b2b"), value: "b2b" },
    { name: $pt("form_type_option_product"), value: "product" },
    { name: $pt("form_type_option_price"), value: "price" },
  ];

  const validator = useMemo(() => {
    switch (formType.current) {
      case "order":
        return orderWovarContactUsValidator;
      case "b2b":
        return b2bWovarContactUsValidator;
      case "product":
        return productWovarContactUsValidator;
      case "price":
        return baseWovarContactUsValidator;
      default:
        return baseWovarContactUsValidator;
    }
  }, [formType.current]);

  const isOrderOrB2b = useMemo(() => {
    return ["order", "b2b"].includes(formType.current);
  }, [formType.current]);

  return (
    <div key={formRef} className="form">
      <ValidatedForm
        validator={validator}
        id={formId}
        method="post"
        action={localePath("/contact")}
        onSubmit={(data) => {
          gtmContactForm({
            email: data.email || "",
            phone_number: data.phone || "",
            first_name: data.name || "",
          });
        }}
      >
        <h3 className="heading sans-serif mdl strong underlined left pb-3 mb-3">
          {title}
        </h3>
        <WvFormHiddenInputs
          data={{
            _action: ContactUsAction.CONTACT_US,
            redirect: localePath(linkToAfterSubmit),
          }}
        />

        <div className="row">
          <WvSelectField
            name="formType"
            list={formTypes}
            defaultValue={formType.current}
            placeholder={$pt("contact_form_type_label")}
            label={$pt("contact_form_type_label")}
            onChanged={(val) => {
              setFormRef(Math.random());
              formType.current = val;
            }}
          />
          <WvField
            name="name"
            label={$pt("name_placeholder")}
            required
            fieldClass="col-12 col-sm-6"
          >
            <WvInputField
              initialValue=""
              name="name"
              placeholder={$pt("name_placeholder")}
              type="text"
            />
          </WvField>
          <WvField
            name="companyName"
            label={
              $pt("company_name_placeholder") +
              (formType.current !== "b2b" ? ` (${$pt("form_optional")})` : "")
            }
            fieldClass="col-12 col-sm-6"
            required={formType.current === "b2b"}
          >
            <WvInputField
              initialValue=""
              name="companyName"
              placeholder={
                $pt("company_name_placeholder") +
                (formType.current !== "b2b" ? ` (${$pt("form_optional")})` : "")
              }
              type="text"
            />
          </WvField>
          <WvField
            name="email"
            label={$pt("contact_email_placeholder")}
            required
            fieldClass="col-12 col-sm-6"
          >
            <WvInputField
              initialValue=""
              name="email"
              placeholder={$pt("contact_email_placeholder")}
              type="email"
            />
          </WvField>
          <WvField
            name="phone"
            label={$pt("contact_phone_placeholder")}
            required
            fieldClass="col-12 col-sm-6"
          >
            <WvInputField
              initialValue=""
              name="phone"
              placeholder={$pt("contact_phone_placeholder")}
              type="tel"
            />
          </WvField>
          <WvField
            name={isOrderOrB2b ? "orderNumber" : "articleCode"}
            label={
              $pt(
                isOrderOrB2b
                  ? "order_number_placeholder"
                  : "article_code_placeholder"
              ) +
              (!["order", "product"].includes(formType.current)
                ? ` (${$pt("form_optional")})`
                : "")
            }
            fieldClass="col-12"
            required={["order", "product"].includes(formType.current)}
          >
            <WvInputField
              initialValue=""
              name={isOrderOrB2b ? "orderNumber" : "articleCode"}
              placeholder={
                $pt(
                  isOrderOrB2b
                    ? "order_number_placeholder"
                    : "article_code_placeholder"
                ) +
                (!["order", "product"].includes(formType.current)
                  ? ` (${$pt("form_optional")})`
                  : "")
              }
              type="text"
            />
          </WvField>
          <WvTextareaField
            name="comment"
            label={$pt("contact_comment_placeholder")}
            required
            fieldClass="col-12"
            placeholder={$pt("contact_comment_placeholder")}
            rows={5}
            minLength={10}
          />
          <input id="recaptchaInput" type="hidden" name="recaptchaValue" />
          <div className="col-12  mb-3">
            <div className="checkbox">
              <label>
                <input type="checkbox" value="1" name="newsletter" />{" "}
                {$pt("subscribe_newletter")}
              </label>
            </div>
          </div>
          <div className="col-12 mb-3 text-right">
            <GoogleReCaptchaCheckbox onChange={handleRecaptchaChange} />

            <small className="mr-3">* {$pt("contact_compulsory_field")}</small>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={!recaptchaValue}
            >
              {loading ? (
                <>
                  <i data-icon-before="" className="zmdi-hc-spin mr-3"></i>{" "}
                  {$pt("register_label_submitting")}
                </>
              ) : (
                <>
                  {" "}
                  <span>{$pt("contact_submit_button")}</span>{" "}
                  <i className="zmdi zmdi-chevron-right"></i>
                </>
              )}
            </button>
          </div>
        </div>
      </ValidatedForm>
    </div>
  );
};
export default WvContactUsForm;
